// How a place is worked out from the `event_driver_points` view.
//
// A place is always ranked on the points total, never on the round a driver went out in. Two drivers who
// went out in the same round can hold different totals (the best reaction time award is worth 7, or 11 for
// a perfect 0.000), and drivers who paid but never raced still hold the 5 point entry award, so they place
// too instead of showing a dash.

// The fields any of the helpers below need from a row of the view
export interface RaceDayPoints {
  event_id: string;
  category_name: string;
  driver_key: string;
  total_points: number;
}

// A re-entry is stored as its own category ("Street Bikes Re-Entry"), so strip the suffix to get the
// category a driver is really placed in.
export function canonicalCategoryName(name: string) {
  return name.replace(/\s*re[- ]?(entry|enter)$/i, '').trim();
}

// Places inside one standings table: the highest total is 1st, equal totals share a place and the numbers
// stay dense (1, 2, 2, 3), so a driver who scored more is never shown level with one who scored less.
// Sorts the array into standings order as well.
export function assignPlacesByPoints<T extends { total_points: number; position: number }>(entries: T[]) {
  entries.sort((a, b) => b.total_points - a.total_points);

  let place = 0;
  let previousPoints = Number.NaN;

  for (const entry of entries) {
    if (entry.total_points !== previousPoints) {
      place += 1;
      previousPoints = entry.total_points;
    }
    entry.position = place;
  }
}

// The place each driver took in their category on each race day, keyed by raceDayPlaceKey()
export function placesByRaceDay(rows: RaceDayPoints[]) {
  const totalsByRaceDay = new Map<string, Map<string, number>>();

  for (const row of rows) {
    const raceDay = `${row.event_id}|${canonicalCategoryName(row.category_name || 'Unknown Category')}`;
    if (!totalsByRaceDay.has(raceDay)) totalsByRaceDay.set(raceDay, new Map());

    // A driver's re-entry row belongs to the same category, so add it to their total for the day
    const totals = totalsByRaceDay.get(raceDay)!;
    totals.set(row.driver_key, (totals.get(row.driver_key) || 0) + (row.total_points || 0));
  }

  const places = new Map<string, number>();

  for (const [raceDay, totals] of totalsByRaceDay) {
    const entries = Array.from(totals, ([driverKey, total]) => ({
      driverKey,
      total_points: total,
      position: 0
    }));
    assignPlacesByPoints(entries);

    for (const entry of entries) {
      places.set(`${raceDay}|${entry.driverKey}`, entry.position);
    }
  }

  return places;
}

export function raceDayPlaceKey(row: RaceDayPoints) {
  return `${row.event_id}|${canonicalCategoryName(row.category_name || 'Unknown Category')}|${row.driver_key}`;
}
